import { MessageSquare, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CATEGORY_DISPLAY_ORDER } from '../interviews.types';
import type { QuestionCategory, WizardQuestion } from '../interviews.types';

import { Button } from '~/components/ui/button';
import { Separator } from '~/components/ui/separator';

type ConfirmJob = {
  title: string;
  description?: string | null;
  atsExternalJobId?: string | null;
  location?: string | null;
};

type ConfirmStepProps = {
  jobs: ConfirmJob[];
  questions: WizardQuestion[];
  modality: string[];
  languages: string[];
  communicationLanguage: string;
  onBack: () => void;
  onGoLive: () => void;
  isSubmitting: boolean;
};

const MODALITY_ICONS: Record<string, React.ReactNode> = {
  text: <MessageSquare className="size-4" />,
  voice: <Phone className="size-4" />
};

const MODALITY_LABELS: Record<string, string> = {
  text: 'wizard.basicInfo.modalityText',
  voice: 'wizard.basicInfo.modalityVoice'
};

export function ConfirmStep({
  jobs,
  questions,
  modality,
  languages,
  communicationLanguage,
  onBack,
  onGoLive,
  isSubmitting
}: ConfirmStepProps): React.ReactNode {
  const { t } = useTranslation();

  const questionsByCategory = CATEGORY_DISPLAY_ORDER.reduce<Record<QuestionCategory, number>>(
    (acc, cat) => {
      acc[cat] = questions.filter((q) => q.category === cat).length;
      return acc;
    },
    { eligibility: 0, nice_to_have: 0, job_preview: 0, assessment: 0, information: 0 }
  );

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-4">
      <div className="flex flex-1 min-h-0 flex-col gap-4 overflow-y-auto">
        {/* Job list */}
        <div className="flex flex-col gap-2">
          {jobs.map((job) => (
            <div key={job.title} className="rounded-lg border border-border bg-card p-4 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <p className="text-sm font-semibold">{job.title}</p>
                    {job.location && (
                      <span className="text-xs text-muted-foreground">{job.location}</span>
                    )}
                  </div>
                  {job.description && (
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                      {job.description
                        .replace(/<[^>]*>/g, ' ')
                        .replace(/\s+/g, ' ')
                        .trim()}
                    </p>
                  )}
                </div>
                {job.atsExternalJobId && (
                  <span className="shrink-0 font-mono text-xs text-muted-foreground">
                    #{job.atsExternalJobId}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Shared settings */}
        <div className="rounded-lg border border-border bg-card shadow-sm">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 p-5">
            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('wizard.confirm.modality')}
              </p>
              <div className="flex flex-col gap-1">
                {modality.map((m) => (
                  <div key={m} className="flex items-center gap-1.5 text-sm">
                    <span className="text-muted-foreground">{MODALITY_ICONS[m]}</span>
                    {(t as (k: string) => string)(MODALITY_LABELS[m] ?? m)}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('wizard.confirm.languages')}
              </p>
              <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                {languages.map((code) => (
                  <span key={code} className="text-sm">
                    {(t as (k: string) => string)(`screening.languages.${code}`)}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('wizard.basicInfo.communicationLanguage')}
              </p>
              <span className="text-sm">
                {(t as (k: string) => string)(`screening.languages.${communicationLanguage}`)}
              </span>
            </div>

            <div className="col-span-2">
              <Separator className="mb-4" />
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('wizard.confirm.questionsCount', { count: questions.length })}
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {CATEGORY_DISPLAY_ORDER.filter((cat) => questionsByCategory[cat] > 0).map((cat) => (
                  <span key={cat} className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{questionsByCategory[cat]}</span>{' '}
                    {(t as (k: string) => string)(`wizard.questions.categories.${cat}`)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">{t('wizard.confirm.goLiveHint')}</p>
      </div>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          {t('wizard.back')}
        </Button>
        <Button onClick={onGoLive} disabled={isSubmitting}>
          {t('wizard.goLive')}
        </Button>
      </div>
    </div>
  );
}
